import {
  Component,
  DoCheck,
  ElementRef, forwardRef,
  HostBinding,
  Input, OnDestroy, OnInit,
  Optional,
  Self
} from '@angular/core';
import { GasNetworkTariffModel } from '@core/api';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NgControl,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Subject } from 'rxjs/index';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { MatFormFieldControl } from '@angular/material';

export const DEFAULT_DURATION_RENT_VALUE = 1;
export const DEFAULT_DURATION_RENT_TYPE = GasNetworkTariffModel.TypeEnum.FixedHours;

@Component({
  selector: 'duration-rent-input',
  templateUrl: './duration-rent-input.component.html',
  styleUrls: ['./duration-rent-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => DurationRentInputComponent)
    }
  ]
})
export class DurationRentInputComponent implements OnInit, DoCheck, OnDestroy, ControlValueAccessor {

  static nextId = 0;

  private _placeholder: string;
  private _required = false;
  private _disabled = false;

  @HostBinding() id = `duration-rent-${ DurationRentInputComponent.nextId++ }`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  focused = false;
  errorState = false;
  controlType = 'duration-rent';

  stateChanges = new Subject<void>();

  @Input() defaultTariffValue: number = DEFAULT_DURATION_RENT_VALUE;

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get empty() {
    return !this.value;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }

  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  get disabled() {
    return this._disabled;
  }

  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }

  @Input()
  set value(value: number) {
    this.userTariffValue = this.getTariffValue(value, this.userTariffType);
    this.stateChanges.next();
  }

  get value(): number {
    return this.getTariffHours(this.userTariffValue, this.userTariffType);
  }

  form: FormGroup = new FormGroup({
    tariff: new FormControl(DEFAULT_DURATION_RENT_VALUE, [Validators.required, Validators.min(11)]),
    type: new FormControl(DEFAULT_DURATION_RENT_TYPE, [Validators.required])
  }, { updateOn: 'change' });

  constructor(
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl
  ) {
    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });

    this.userTariffValue = this.getTariffValue(this.defaultTariffValue, this.userTariffType);

    this.initTypeInput();
    this.initTariffInput();
  }

  get tariffControl(): AbstractControl {
    return this.form.controls['tariff'];
  }

  get typeControl(): AbstractControl {
    return this.form.controls['type'];
  }

  get userTariffValue() {
    return this.tariffControl.value;
  }

  set userTariffValue(value) {
    this.tariffControl.setValue(value);
  }

  get userTariffType() {
    return this.typeControl.value;
  }

  set userTariffType(value) {
    this.typeControl.setValue(value);
  }

  private setDailyValidations() {
    this.updateTariffValidators([Validators.required, Validators.min(1)]);
    this.updateControlValidators([Validators.required, Validators.min(1)]);
  }

  private setHoursValidations() {
    this.updateTariffValidators([Validators.required, Validators.min(1)]);
    this.updateControlValidators([Validators.required, Validators.min(1)]);
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  initTariffInput() {
    this.tariffControl.valueChanges
      .subscribe(value => {
        this.onChange(this.getTariffHours(value, this.userTariffType));
      });
  }

  initTypeInput() {
    this.typeControl.valueChanges
      .subscribe(type => {
        if (type === GasNetworkTariffModel.TypeEnum.FixedHours) {
          this.setHoursValidations();
        } else {
          this.setDailyValidations();
        }
      });
  }

  updateTariffValidators(validators: Array<ValidatorFn>) {
    this.tariffControl.setValidators(validators);
    this.tariffControl.updateValueAndValidity();
    this.tariffControl.markAsTouched()
  }

  updateControlValidators(validators: Array<ValidatorFn>) {
    this.ngControl.control.setValidators(validators);
    this.ngControl.control.updateValueAndValidity();
    this.ngControl.control.markAsTouched()
  }

  getTariffValue(value, type) {
    switch (type) {
      case GasNetworkTariffModel.TypeEnum.FixedHours:
        return value;
      case GasNetworkTariffModel.TypeEnum.Daily:
        return value / 24;
      default:
        return value;
    }
  }

  getTariffHours(value, type) {
    switch (type) {
      case GasNetworkTariffModel.TypeEnum.FixedHours:
        return value;
      case GasNetworkTariffModel.TypeEnum.Daily:
        return value * 24;
      default:
        return value;
    }
  }

  changeRentHours() {
    if (this.form.controls.type.value === GasNetworkTariffModel.TypeEnum.FixedHours) {
      this.userTariffValue = 1;
    }

    this.onChange(this.getTariffHours(this.userTariffValue, this.userTariffType));
  }

  /*onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }*/

  onBlur() {
    this.ngControl.control.setValue(this.ngControl.control.value);
  }

  writeValue(value) {
    this.value = value;

    this.onChange(value);
    this.onTouched();
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  ngOnInit() {
    this.setDailyValidations();
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;

      this.stateChanges.next();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

}
